// ─────────────────────────────────────────────────────────────────
// V2 API Service — hybrid mock / real
//
// Each function returns a Promise of platform data. Originally the
// whole file was mock-backed (mockData.js with simulated latency).
//
// Milestone bridge plan: as each backend ships, we swap the matching
// service function to a real axiosInstance call. **Real API failures
// must fall back to mock** so the dashboard still renders during
// backend outages or while a milestone is still in flight.
//
// Currently real:
//   ✓ M5 — getTemplateVersions, searchLibrary, getVersion,
//          restoreVersion, getVersionDiff
//   ✓ M7 — getCertificates, uploadCertificate, setDefaultCertificate,
//          revokeCertificate (signed PDFs list still mock — separate
//          backend epic, not wired in M7 Phase 4)
//
// Still mock (will swap as milestones close):
//   • M4 endpoints list (could swap to /swagger/v2-public)
//   • M6 SDK info
//   • M7 signed PDFs (waits on per-cert sign telemetry)
//   • M8 audit log
//   • M8.5 storage stats
//   • M8.7 invitations + team
//   • Account/usage overview (could swap to /api/v2/plans/me)
// ─────────────────────────────────────────────────────────────────

import axiosInstance from '../../components/token/axiosInstance';
import * as data from '../mock/data';

const LATENCY_MS = 300; // simulated round-trip for the still-mock paths

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Base URL helper ─────────────────────────────────────────────
//
// Every existing V1 call in the codebase resolves its base URL the
// same way:
//   window.GET_API_URL(process.env.REACT_APP_SERVICE) + '/api/...'
//
// In development .env.development sets REACT_APP_SERVICE to a full
// origin (http://localhost:44371). In production .env sets it to a
// reverse-proxy path ('srv'). The GET_API_URL helper (defined in
// public/env-config.js so it can be swapped at deploy time without
// rebuilding) is currently a passthrough.
//
// We use the same primitive so V2 URLs work in every environment
// the V1 ones already work in, including the IIS/nginx reverse-proxy
// setups used in production deploys.
const apiUrl = (path) =>
    (typeof window !== 'undefined' && window.GET_API_URL
        ? window.GET_API_URL(process.env.REACT_APP_SERVICE)
        : (process.env.REACT_APP_SERVICE || '')) + path;

// ─── Shared error helper ─────────────────────────────────────────
//
// Real API failures are intentionally non-fatal here — the dashboard
// is a read-only overview; if a backend is down we'd rather show stale
// mock data than a blank panel. Every real call wraps its body in
// try/catch and falls back to the mock equivalent.
const logFallback = (op, err) => {
    console.warn(`[v2ApiService] ${op} fell back to mock`, err?.message || err);
};

// ─── Account & Usage (still mock) ────────────────────────────────
export const getAccount = async () => {
    await delay(LATENCY_MS);
    return { ...data.mockAccount };
};

export const getUsageSnapshot = async () => {
    await delay(LATENCY_MS);
    return { ...data.mockUsageSnapshot };
};

// ─── M4  OpenAPI + Docs (still mock; future: pull from spec) ────
export const getApiEndpoints = async () => {
    await delay(LATENCY_MS);
    return [...data.mockApiEndpoints];
};

// ═════════════════════════════════════════════════════════════════
// M5 — Versioning + Library + Search   (REAL BACKEND)
// ═════════════════════════════════════════════════════════════════
//
// Endpoint mapping:
//   getTemplateVersions    → multiple GET /api/v2/templates/{id}/versions
//                            (the mock returns versions grouped by template;
//                             we recreate that shape by walking every template)
//   searchLibrary          → GET /api/v2/templates/search?q=
//   getTemplateVersion     → GET /api/v2/templates/{id}/versions/{v}
//   restoreTemplateVersion → POST /api/v2/templates/{id}/versions/{v}/restore
//   getTemplateVersionDiff → GET /api/v2/templates/{id}/versions/{v1}/diff/{v2}
// ═════════════════════════════════════════════════════════════════

/**
 * Lists templates AND their version histories grouped by template.
 *
 * The mock shape is: [{ templateId, name, versions: [{...}, ...] }, ...].
 * We replicate it by:
 *   1. Listing all templates in the account.
 *   2. For each template, fetching /versions.
 *   3. Merging.
 *
 * That's N+1 requests, but the dashboard is a low-traffic page and the
 * version list is projected (no designerJson), so each per-template call
 * is small. If template count grows to thousands, we'd add a bulk endpoint;
 * for the M5 first cut, N+1 is fine.
 */
export const getTemplateVersions = async () => {
    try {
        // Step 1: get all templates.
        const templatesRes = await axiosInstance.get(apiUrl('/api/v2/templates'));
        const templates = templatesRes.data?.templates ?? [];

        // Empty account → return empty array, not the mock fallback —
        // a real, intentionally-empty list is different from "API down".
        if (templates.length === 0) return [];

        // Step 2: fetch each template's versions in parallel.
        const groups = await Promise.all(
            templates.map(async (t) => {
                try {
                    const vRes = await axiosInstance.get(apiUrl(`/api/v2/templates/${t.id}/versions`));
                    const versions = vRes.data?.versions ?? [];
                    return {
                        templateId: t.id,
                        name: t.name,
                        versions: versions.map((v) => ({
                            version: v.version,
                            createdAt: v.createdAt,
                            author: v.createdByEmail,
                            changeNote: v.changeNote,
                            isCurrent: v.isCurrent,
                        })),
                    };
                } catch (perTemplateErr) {
                    // Per-template failure: include the template with empty versions
                    // rather than failing the whole batch.
                    console.warn(`[v2ApiService] versions for ${t.id} failed`, perTemplateErr?.message);
                    return { templateId: t.id, name: t.name, versions: [] };
                }
            })
        );

        // Filter out templates that genuinely have no versions (newer than M5
        // backend deployment — their first save happens before snapshot logic
        // existed). Mock fallback for those entries would be wrong.
        return groups.filter((g) => g.versions.length > 0);
    } catch (err) {
        logFallback('getTemplateVersions', err);
        return [...data.mockTemplateVersions];
    }
};

/**
 * Library search across the account's templates (+ public marketplace).
 * Empty query returns all (matches mock semantics).
 */
export const searchLibrary = async (query) => {
    try {
        const q = (query || '').trim();
        const url = q
            ? apiUrl(`/api/v2/templates/search?q=${encodeURIComponent(q)}`)
            : apiUrl('/api/v2/templates/search');

        const res = await axiosInstance.get(url);
        const templates = res.data?.templates ?? [];

        // Reshape to mock format: { id, name, category, tags, version }.
        // The backend doesn't return category/tags yet (future feature),
        // so we synthesize from documentType. version comes from list item
        // (Variables/Assets count are reflected in lookups, not search).
        return templates.map((t) => ({
            id: t.id,
            name: t.name,
            category: t.documentType || 'general',
            tags: t.documentType ? [t.documentType] : [],
            // List endpoint doesn't return a "current version" field today —
            // best-effort placeholder, the M5 panel only displays it as
            // metadata.
            version: 1,
        }));
    } catch (err) {
        logFallback('searchLibrary', err);
        // Fall back to client-side mock search (matches old behavior).
        if (!query || !query.trim()) return [...data.mockLibrarySearchResults];
        const q = query.toLowerCase();
        return data.mockLibrarySearchResults.filter(
            (t) =>
                t.name.toLowerCase().includes(q) ||
                t.tags.some((tag) => tag.toLowerCase().includes(q)) ||
                t.category.toLowerCase().includes(q)
        );
    }
};

/**
 * Fetch a single template version (full snapshot — includes designerJson).
 * No mock fallback: this is only called on explicit user action (clicking
 * a version row) — if the backend is down, surfacing the error is correct.
 */
export const getTemplateVersion = async (templateId, version) => {
    const res = await axiosInstance.get(
        apiUrl(`/api/v2/templates/${templateId}/versions/${version}`)
    );
    return res.data;
};

/**
 * Restore a template to a previous version. Creates a new version on top
 * of the history (non-destructive).
 */
export const restoreTemplateVersion = async (templateId, version, changeNote) => {
    const res = await axiosInstance.post(
        apiUrl(`/api/v2/templates/${templateId}/versions/${version}/restore`),
        { changeNote: changeNote || null }
    );
    return res.data;
};

/**
 * Element-level diff between two versions of a template.
 * Returns { templateId, fromVersion, toVersion, addedCount, removedCount,
 *           modifiedCount, entries: [{ changeType, elementId, ... }] }.
 */
export const getTemplateVersionDiff = async (templateId, fromVersion, toVersion) => {
    const res = await axiosInstance.get(
        apiUrl(`/api/v2/templates/${templateId}/versions/${fromVersion}/diff/${toVersion}`)
    );
    return res.data;
};

// ─── M6  SDKs (still mock) ───────────────────────────────────────
export const getSdkInfo = async () => {
    await delay(LATENCY_MS);
    return { ...data.mockSdkInfo };
};

// ─── M7  Signed PDF + Cert Management ────────────────────────────
//
// Cert lifecycle backend lives at /api/v2/certificates and ships in
// M7 Phase 1+2 (Day 11). Phase 4 wires the dashboard up to it.
//
// Backend response shape (per CertificateService.MapToListItem):
//   { id, name, subject, issuer, serialNumber, thumbprint,
//     notBefore, notAfter, signatureAlgorithm, keySize,
//     isDefault, isActive, createdAt, revokedAt }
//
// The legacy mock used validFrom/validTo/status/algorithm. The list
// endpoint normalizes to the new shape; the M7Panel reads either
// (defensive on legacy mock fallback). Date fields arrive as ISO
// strings either way.
export const getCertificates = async () => {
    try {
        const res = await axiosInstance.get(apiUrl('/api/v2/certificates'));
        // Backend envelope: { count, certificates: [...] }
        const list = res.data?.certificates ?? [];
        return list;
    } catch (err) {
        logFallback('getCertificates', err);
        return [...data.mockCertificates];
    }
};

/**
 * Upload a PKCS#12 (.pfx/.p12) certificate. The PFX is base64-encoded
 * before send. Backend rejects with 400 + `invalid_pfx` when password
 * is wrong or the bundle has no private key; we surface the detail to
 * the user verbatim.
 *
 * NO mock fallback: this is a user-initiated write — if backend is
 * down, the upload genuinely failed and we should surface the error,
 * not silently fake a success.
 */
export const uploadCertificate = async ({ pfxBase64, password, name }) => {
    const res = await axiosInstance.post(
        apiUrl('/api/v2/certificates'),
        {
            pfxBase64,
            password,
            name: name?.trim() || null,
        }
    );
    return res.data;
};

/**
 * Mark a certificate as the account default. Atomic on the backend
 * (clears previous default + sets new in one call). Returns
 * { success: true } on 200, throws on 404 (revoked or not found).
 */
export const setDefaultCertificate = async (id) => {
    const res = await axiosInstance.post(
        apiUrl(`/api/v2/certificates/${id}/set-default`)
    );
    return res.data;
};

/**
 * Soft-revoke a certificate. Backend sets IsActive=false + RevokedAt.
 * The certificate remains in the list (for audit) but is greyed out
 * and no longer eligible for signing.
 */
export const revokeCertificate = async (id) => {
    const res = await axiosInstance.delete(
        apiUrl(`/api/v2/certificates/${id}`)
    );
    return res.data;
};

// Signed PDFs telemetry is a separate backend epic — keep the mock
// in place so the M7Panel's lower table still renders.
export const getSignedPdfs = async () => {
    await delay(LATENCY_MS);
    return [...data.mockSignedPdfs];
};

// ─── M8  Audit Log (still mock) ──────────────────────────────────
export const getAuditLog = async (filters) => {
    await delay(LATENCY_MS);
    let logs = [...data.mockAuditLog];
    if (filters) {
        if (filters.action) logs = logs.filter((l) => l.action === filters.action);
        if (filters.severity) logs = logs.filter((l) => l.severity === filters.severity);
        if (filters.actor) logs = logs.filter((l) => l.actor === filters.actor);
    }
    return logs;
};

// ─── M8.5  Storage + Backup (still mock) ─────────────────────────
export const getStorageInfo = async () => {
    await delay(LATENCY_MS);
    return { ...data.mockStorageInfo };
};

// ─── M8.7  Email + Invitation (still mock) ──────────────────────
export const getInvitations = async () => {
    await delay(LATENCY_MS);
    return [...data.mockInvitations];
};

export const getTeamMembers = async () => {
    await delay(LATENCY_MS);
    return [...data.mockTeamMembers];
};

export const sendInvitation = async ({ email, role }) => {
    await delay(LATENCY_MS);
    return {
        id: 'inv_' + Date.now(),
        email,
        role,
        invitedBy: data.mockAccount.email,
        invitedAt: new Date().toISOString(),
        status: 'pending',
        acceptedAt: null,
    };
};

// ─── Shared: Plans, API Keys, Webhooks, Bulk Jobs (still mock) ───
export const getPlans = async () => {
    await delay(LATENCY_MS);
    return [...data.mockPlans];
};

export const getApiKeys = async () => {
    await delay(LATENCY_MS);
    return [...data.mockApiKeys];
};

export const getWebhooks = async () => {
    await delay(LATENCY_MS);
    return [...data.mockWebhooks];
};

export const getBulkJobs = async () => {
    await delay(LATENCY_MS);
    return [...data.mockBulkJobs];
};
